import { Delete } from "@mui/icons-material";
import { Grid, IconButton, Paper, styled, Typography } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxExpansion from "components/UI/AcxExpansion";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import type { LLMPromptTemplate } from "models/LLMs/LLMPromptTemplate";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LLMPromptTemplatesService } from "services/LLMPromptTemplatesService";
import { useStore } from "utils/useStore";

const Header = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.gray[700],
    fontSize: "24px",
}));

export const OrganizationLLMPromptTemplates = observer(() => {
    const [llmPromptTemplates, setLLMPromptTemplates] = useState<
        LLMPromptTemplate[]
    >([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    // holds the id of the template targeted for deleting (deactivating)
    const [isDeleteLoading, setIsDeleteLoading] = useState<string>();

    const service = useMemo(() => new LLMPromptTemplatesService(), []);

    const messageStore = useStore(MessageStore);

    const loadPromptTemplates = useCallback(
        async (abortSignal?: AbortSignal) => {
            try {
                setIsLoadingTemplates(true);
                const templates = await service.getLLMPromptTemplates(
                    abortSignal,
                );
                setLLMPromptTemplates(templates);
            } catch (error) {
                if (error instanceof Error) {
                    messageStore.logError(error.message);
                }
            } finally {
                setIsLoadingTemplates(false);
            }
        },
        [service, messageStore],
    );

    const createPromptTemplate = useCallback(
        async (create: LLMPromptTemplate) => {
            try {
                const template = await service.createLLMPromptTemplate(create);
                setLLMPromptTemplates((previous) => {
                    return [template, ...previous];
                });
                messageStore.logMessage("Prompt template created", "success");
            } catch (error) {
                if (error instanceof Error) {
                    messageStore.logError(error.message);
                }
            }
        },
        [service, messageStore],
    );

    const updatePromptTemplate = useCallback(
        async (update: LLMPromptTemplate) => {
            try {
                if (!update.id) return;
                const template = await service.updateLLMPromptTemplate(update);
                setLLMPromptTemplates((previous) => {
                    const index = previous.findIndex(
                        (t) => t.id === template.id,
                    );
                    const next = [...previous];
                    next[index] = template;
                    return next;
                });
                messageStore.logMessage("Prompt template updated", "success");
            } catch (error) {
                if (error instanceof Error) {
                    messageStore.logError(error.message);
                }
            }
        },
        [service, messageStore],
    );

    const deletePromptTemplate = useCallback(
        async (id: string) => {
            try {
                if (!id) return;
                setIsDeleteLoading(id);
                await service.deleteLLMPromptTemplate(id);
                setLLMPromptTemplates((previous) => {
                    const next = [...previous];
                    return next.filter((t) => t.id !== id);
                });
                messageStore.logMessage("Prompt template deleted", "success");
            } catch (error) {
                if (error instanceof Error) {
                    messageStore.logError(error.message);
                }
            } finally {
                setIsDeleteLoading(undefined);
            }
        },
        [service, messageStore],
    );

    useEffect(() => {
        const abortController = new AbortController();
        loadPromptTemplates(abortController.signal);

        return () => abortController.abort();
    }, [loadPromptTemplates]);

    return (
        <Grid
            container
            direction="column"
            rowGap={2}
            paddingBlock={5}
            paddingInline={10}
        >
            <Grid
                container
                item
                justifyContent="space-between"
                alignItems="center"
                flexWrap="nowrap"
                columnGap={2}
            >
                <Grid item>
                    <Header>Prompt Templates</Header>
                </Grid>
                <Grid item>
                    <AcxButton
                        color="white"
                        disabled={isAdding}
                        onClick={() => setIsAdding(true)}
                    >
                        Add Prompt Template
                    </AcxButton>
                </Grid>
            </Grid>
            {isLoadingTemplates && (
                <Grid item>
                    <AcxLoadingIndicator alternate="PuffLoader" size={64} />
                </Grid>
            )}
            {isAdding && (
                <Grid item>
                    <LLMPromptTemplateEdit
                        llmPromptTemplate={{
                            id: "",
                            name: "",
                            description: "",
                            template: "",
                        }}
                        onSave={async (template) => {
                            await createPromptTemplate(template);
                            setIsAdding(false);
                        }}
                    />
                </Grid>
            )}
            {!isLoadingTemplates &&
                llmPromptTemplates
                    .sort((a, b) => {
                        if (!a.modifiedOn && !b.modifiedOn) return -1;
                        if (!a.modifiedOn) return -1;
                        if (!b.modifiedOn) return 1;
                        return (
                            new Date(b.modifiedOn).getTime() -
                            new Date(a.modifiedOn).getTime()
                        );
                    })
                    .map((template) => (
                        <Grid item key={template.id}>
                            <AcxExpansion
                                headerSx={(theme) => ({
                                    paddingInline: theme.spacing(1),
                                    paddingBlock: theme.spacing(2),
                                    opacity:
                                        isDeleteLoading === template.id
                                            ? "75%"
                                            : "100%",
                                    height: "24px",
                                })}
                                header={
                                    <Grid
                                        container
                                        padding={1}
                                        paddingBlock={2}
                                        sx={{
                                            height: "24px",
                                            display: "flex",
                                            alignContent: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Grid item sx={{display: "flex", alignItems: "center"}}>
                                            <Typography
                                                sx={(theme) => ({
                                                    fontWeight: 500,
                                                    fontSize: "14px",
                                                    lineHeight: "20px",
                                                    color: theme.palette
                                                        .gray[700],
                                                })}
                                            >
                                                {template.name}
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            container
                                            item
                                            columnGap={1}
                                            alignItems="center"
                                            width="fit-content"
                                            flexWrap="nowrap"
                                        >
                                            {template.createdBy &&
                                                template.createdOn &&
                                                !template.modifiedBy && (
                                                    <Grid item>
                                                        <Typography
                                                            sx={(theme) => ({
                                                                fontSize:
                                                                    "12px",
                                                                color: theme
                                                                    .palette
                                                                    .gray[500],
                                                            })}
                                                        >
                                                            Created:{" "}
                                                            {moment
                                                                .utc(
                                                                    template.createdOn,
                                                                )
                                                                .local()
                                                                .format(
                                                                    "lll",
                                                                )}{" "}
                                                            {template.createdBy}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            {template.modifiedBy &&
                                                template.modifiedOn && (
                                                    <Grid item>
                                                        <Typography
                                                            sx={(theme) => ({
                                                                fontSize:
                                                                    "12px",
                                                                color: theme
                                                                    .palette
                                                                    .gray[500],
                                                            })}
                                                        >
                                                            Last modified:{" "}
                                                            {moment
                                                                .utc(
                                                                    template.modifiedOn,
                                                                )
                                                                .local()
                                                                .format(
                                                                    "lll",
                                                                )}{" "}
                                                            {
                                                                template.modifiedBy
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            <Grid item>
                                                <IconButton
                                                    size="small"
                                                    disabled={!!isDeleteLoading}
                                                    onClick={() =>
                                                        deletePromptTemplate(
                                                            template.id,
                                                        )
                                                    }
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                }
                                body={
                                    <LLMPromptTemplateEdit
                                        llmPromptTemplate={template}
                                        onSave={(template) =>
                                            updatePromptTemplate(template)
                                        }
                                    />
                                }
                            />
                        </Grid>
                    ))}
        </Grid>
    );
});

interface LLMPromptTemplateProps {
    llmPromptTemplate: LLMPromptTemplate;
    onSave(template: LLMPromptTemplate): void | Promise<void>;
}

function LLMPromptTemplateEdit(props: LLMPromptTemplateProps) {
    const [intermediateTemplate, setIntermediateTemplate] = useState(
        props.llmPromptTemplate,
    );
    const [isLoading, setIsLoading] = useState(false);

    useEffect(
        () => setIntermediateTemplate(props.llmPromptTemplate),
        [props.llmPromptTemplate],
    );

    const set = useCallback(
        <K extends keyof LLMPromptTemplate>(
            key: K,
            value: LLMPromptTemplate[K],
        ) => {
            setIntermediateTemplate((previous) => {
                return { ...previous, [key]: value };
            });
        },
        [],
    );

    const canSave = useMemo(() => {
        const hasChanged =
            intermediateTemplate.name !== props.llmPromptTemplate.name ||
            intermediateTemplate.description !==
                props.llmPromptTemplate.description ||
            intermediateTemplate.template !== props.llmPromptTemplate.template;
        const hasRequiredFields =
            !!intermediateTemplate.name && !!intermediateTemplate.template;
        return hasChanged && hasRequiredFields;
    }, [intermediateTemplate, props.llmPromptTemplate]);

    return (
        <Paper>
            <Grid container direction="column" rowGap={2} padding={2}>
                <Grid
                    container
                    item
                    columnGap={2}
                    columns={2}
                    flexWrap="nowrap"
                >
                    <Grid item xs={1}>
                        <AcxMainTextField
                            id={`${props.llmPromptTemplate.id}-name`}
                            labelText="Name"
                            placeholderText="Enter template name..."
                            required
                            fullWidth
                            value={intermediateTemplate.name}
                            onChange={(event) =>
                                set("name", event.target.value)
                            }
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <AcxMainTextField
                            id={`${props.llmPromptTemplate.id}-description`}
                            labelText="Description"
                            placeholderText="Enter template description..."
                            fullWidth
                            value={intermediateTemplate.description}
                            onChange={(event) =>
                                set("description", event.target.value)
                            }
                        />
                    </Grid>
                </Grid>
                <Grid item>
                    <AcxMainTextField
                        id={`${props.llmPromptTemplate.id}-template`}
                        labelText="Template"
                        placeholderText="Enter template..."
                        fullWidth
                        multiline
                        rowsMin={16}
                        rowsMax={20}
                        required
                        value={intermediateTemplate.template}
                        onChange={(event) =>
                            set("template", event.target.value)
                        }
                    />
                </Grid>
                <Grid item alignSelf="flex-end">
                    <AcxButton
                        disabled={!canSave}
                        loading={isLoading}
                        onClick={async () => {
                            setIsLoading(true);
                            await props.onSave(intermediateTemplate);
                            setIsLoading(false);
                        }}
                    >
                        Save
                    </AcxButton>
                </Grid>
            </Grid>
        </Paper>
    );
}
